import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Layout, Spin } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { useAppDispatch } from '../../utils/redux/store'
import { attemptGetPlaylistsList, PlaylistOrderBy } from '../../utils/redux/playlistSlice'
import { apiBaseURL } from '../../utils/api'

import './PlaylistTagPage.scss'

const { Content } = Layout

type UploadedDestination = 'styles' | 'moods' | 'daytimes' | 'businesses'
type ThemeMode = 'light' | 'dark'

function isUploadedDestination(v: string): v is UploadedDestination {
  return v === 'styles' || v === 'moods' || v === 'daytimes' || v === 'businesses'
}

function detectThemeMode(): ThemeMode {
  if (typeof document === 'undefined') return 'dark'

  const html = document.documentElement
  const body = document.body

  const attrs = [
    body.getAttribute('data-theme'),
    html.getAttribute('data-theme'),
    body.getAttribute('theme'),
    html.getAttribute('theme'),
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())

  let storageTheme = ''
  try {
    storageTheme = String(localStorage.getItem('theme') || '').toLowerCase()
  } catch {
    // noop
  }

  const isLightAttr = attrs.some((v) => v.includes('light'))
  const isLightClass =
    body.classList.contains('light') ||
    html.classList.contains('light') ||
    body.classList.contains('theme-light') ||
    html.classList.contains('theme-light')

  const isLightStorage = storageTheme.includes('light')

  return isLightAttr || isLightClass || isLightStorage ? 'light' : 'dark'
}

const DEST_TO_FILTER_KEY: Record<
  UploadedDestination,
  'style' | 'mood' | 'daytime' | 'business'
> = {
  styles: 'style',
  moods: 'mood',
  daytimes: 'daytime',
  businesses: 'business',
}

type PlaylistBrief = {
  _id: string
  title: string
  image?: string
}

export default function PlaylistTagPage() {
  const { uploadedDestination = '', id = '' } = useParams<{
    uploadedDestination: string
    id: string
  }>()

  const { t } = useTranslation('playlist')
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const [themeMode, setThemeMode] = useState<ThemeMode>('dark')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<PlaylistBrief[]>([])

  const stateAny = (location.state ?? {}) as any
  const titleFromState = String(stateAny?.title ?? '')
  const fromPlaylistId = String(stateAny?.fromPlaylistId ?? '')

  const validDestination = useMemo(
    () => (isUploadedDestination(uploadedDestination) ? uploadedDestination : null),
    [uploadedDestination],
  )

  useEffect(() => {
    const apply = () => setThemeMode(detectThemeMode())
    apply()

    const html = document.documentElement
    const body = document.body

    const observer = new MutationObserver(() => apply())
    observer.observe(html, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'theme'],
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!validDestination || !id) return

    let cancelled = false

    const run = async () => {
      setLoading(true)
      try {
        const filterKey = DEST_TO_FILTER_KEY[validDestination]
        const payload: any = {
          skip: 0,
          limit: 200,
          // оставил оба ключа на случай несовпадения в API
          oderBy: PlaylistOrderBy.TITLE,
          orderBy: PlaylistOrderBy.TITLE,
          [filterKey]: id,
        }

        const res: any = await dispatch(attemptGetPlaylistsList(payload)).unwrap()

        if (!cancelled) {
          const arr = Array.isArray(res?.playlists) ? res.playlists : []
          setItems(arr)
        }
      } catch {
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [dispatch, id, validDestination])

  const destinationLabel = useMemo(() => {
    switch (validDestination) {
      case 'moods':
        return t('meta.mood', { defaultValue: 'Mood' })
      case 'styles':
        return t('meta.style', { defaultValue: 'Style' })
      case 'daytimes':
        return t('meta.daytime', { defaultValue: 'Daytime' })
      case 'businesses':
        return t('meta.business', { defaultValue: 'Business' })
      default:
        return t('meta.category', { defaultValue: 'Category' })
    }
  }, [validDestination, t])

  const pageTitle = titleFromState || t('meta.category', { defaultValue: 'Category' })

  if (!validDestination) {
    return (
      <Content className={`plc plc--${themeMode}`}>
        <div className="plc__header">
          <button type="button" className="plc__back" onClick={() => navigate(-1)}>
            <LeftOutlined />
          </button>
          <h1 className="plc__title">
            {t('errors.invalidCategory', { defaultValue: 'Invalid category' })}
          </h1>
          <div className="plc__ghost" />
        </div>
      </Content>
    )
  }

  return (
    <Content className={`plc plc--${themeMode}`}>
      <div className="plc__header">
        <button type="button" className="plc__back" onClick={() => navigate(-1)}>
          <LeftOutlined />
        </button>
        <h1 className="plc__title">{pageTitle}</h1>
        <div className="plc__ghost" />
      </div>

      <div className="plc__subline">
        <span className="plc__sublineText">
          {t('meta.playlistsCount', {
            count: items.length,
            defaultValue: `${items.length} playlists`,
          })}
        </span>
      </div>

      {loading ? (
        <div className="plc__loading">
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <div className="plc__empty">
          <div className="plc__emptyTitle">
            {t('empty.noPlaylists', { defaultValue: 'No playlists found' })}
          </div>
          <div className="plc__emptyDesc">
            {t('empty.tryAnotherTag', {
              defaultValue: 'Try another tag or return back.',
            })}
          </div>
        </div>
      ) : (
        <div className="plc__grid">
          {items.map((p) => {
            const img = p.image ? `${apiBaseURL}/uploaded/playlists/${p.image}` : ''
            const isSameAsFrom =
              fromPlaylistId && String(fromPlaylistId) === String(p._id)

            return (
              <button
                key={p._id}
                type="button"
                className={`plc__card ${isSameAsFrom ? 'is-from' : ''}`}
                onClick={() => {
                  if (isSameAsFrom) {
                    navigate(-1)
                    return
                  }

                  navigate(`/playlist/${p._id}`, {
                    state: {
                      fromCategoryId: id,
                      fromCategoryType: validDestination,
                    },
                  })
                }}
              >
                <div className="plc__cover">
                  {img ? (
                    <img
                      className="plc__img"
                      src={img}
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="plc__img plc__img--empty">♪</div>
                  )}

                  <span className="plc__badge">{destinationLabel}</span>
                </div>

                <div className="plc__name" title={p.title}>
                  {p.title}
                </div>

                <div className="plc__meta">
                  <span>{t('actions.open', { defaultValue: 'Open' })}</span>
                  <span className="plc__arrow">→</span>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </Content>
  )
}
